import { defineStore } from 'pinia';
import _ from 'lodash';
import type { FlowStep } from '@Shared';
import type { FlowTask } from '@Types/FlowTask.interface';
import { getClientFlowApi } from '@Api/clientFlow.api';
import {
    FLOW_DEFINITIONS,
    isFeatureFlowType,
} from '@Constants/requestFlow.constants';

// TODO: Create your own flow type for your MFE
import { RequestFlowType } from '@Enums/requestFlowType.enum';


export const useRequestFlowStore = defineStore('requestFlow', {
    state: () => ({
        currentRoute: null as string | null,
        flowSteps: [] as FlowTask[],
        requiredFlows: [] as RequestFlowType[],
        skippedWelcome: false,
    }),
    actions: {

        async loadRequestFlow() {
            const rawFlows = await getClientFlowApi();
            const mappedFlows = this._getRequiredStepsFromFlow(rawFlows);
            this.requiredFlows = mappedFlows;
            this._buildFlowSequence(mappedFlows);
        },

        _getRequiredStepsFromFlow(rawFlows: FlowStep[]): RequestFlowType[] {
            const steps = _.map(rawFlows, (flow) => flow?.stepName?.trim());
            const missingOrBlankFlows = _.filter(steps, (step): step is undefined | '' => !step);
            const nonEmptySteps = _.filter(steps, (step): step is string => Boolean(step));
            const validFlows = _.filter(nonEmptySteps, isFeatureFlowType);
            const invalidFlows = _.reject(nonEmptySteps, isFeatureFlowType);

            if (!_.isEmpty(missingOrBlankFlows)) {
                console.warn('Received request flows with missing or blank stepName from API:', missingOrBlankFlows);
            }

            if (!_.isEmpty(invalidFlows)) {
                console.warn('Received unknown request flows from API:', invalidFlows);
            }

            return validFlows;
        },

        _buildFlowSequence(requiredFlows: RequestFlowType[]) {
            this.flowSteps = [];
            const activeFlows: RequestFlowType[] = [];
            for (const flow of requiredFlows) {
                if (this.skippedWelcome && (flow === RequestFlowType.Welcome || flow === RequestFlowType.WhatsNext)) continue;
                activeFlows.push(flow);
                this.flowSteps.push(...FLOW_DEFINITIONS[flow]);
            }
            this.requiredFlows = activeFlows;
        },

        /** Mark that welcome was skipped (entered via startup from Dashboard). */
        setSkippedWelcome() {
            this.skippedWelcome = true;
        },

        getCurrentFlowTask(): FlowTask | null {
            const currentFlowTask = _.find(this.flowSteps, {
                routeName: this.currentRoute,
            }) as FlowTask | undefined;
            return _.isNil(currentFlowTask)
                ? (_.head(this.flowSteps) ?? null)
                : currentFlowTask;
        },
        getNextFlowTask(): FlowTask | null {
            const currentIndex = _.findIndex(this.flowSteps, {
                routeName: this.currentRoute ?? undefined,
            });
            const nextFlowTask = _.get(this.flowSteps, currentIndex + 1, null);
            this.currentRoute = nextFlowTask?.routeName ?? null;

            return nextFlowTask;
        },
        getPreviousFlowTask(): FlowTask | null {
            const currentIndex = _.findIndex(this.flowSteps, {
                routeName: this.currentRoute ?? undefined,
            });
            const prevFlowTask = _.get(this.flowSteps, currentIndex - 1, null);
            this.currentRoute = prevFlowTask?.routeName ?? null;

            return prevFlowTask;
        },
        setCurrentRoute(routeName: string) {
            // ensure routeName is a valid route in the flow
            const route = _.find(this.flowSteps, { routeName: routeName });
            if (!route) {
                throw new Error(`Route ${routeName} is not part of the current flow`);
            }
            this.currentRoute = routeName;
        },

        hasRouteInFlow(routeName: string): boolean {
            return _.some(this.flowSteps, { routeName });
        },

        _getFlowTypeIndexContainingTaskIndex(taskIndex: number): number {
            let accumulated = 0;
            for (let i = 0; i < this.requiredFlows.length; i++) {
                const tasks = FLOW_DEFINITIONS[this.requiredFlows[i]] ?? [];
                const flowEnd = accumulated + tasks.length - 1;
                if (taskIndex <= flowEnd) return i;
                accumulated += tasks.length;
            }
            return -1;
        },

        _getFirstTaskOfFlowType(flowType: RequestFlowType): FlowTask | null {
            const tasks = FLOW_DEFINITIONS[flowType] ?? [];
            return (_.head(tasks) as FlowTask) ?? null;
        },

        /** Returns the first task of the next flow type (skips rest of current flow). Used when user opts out (e.g. No on MPP purchase). */
        getNextFlowTypeFirstTask(): FlowTask | null {
            const currentIndex = _.findIndex(this.flowSteps, { routeName: this.currentRoute ?? undefined });
            if (currentIndex === -1) return null;

            const flowTypeIndex = this._getFlowTypeIndexContainingTaskIndex(currentIndex);
            if (flowTypeIndex === -1 || flowTypeIndex >= this.requiredFlows.length - 1) return null;

            const nextFlowType = this.requiredFlows[flowTypeIndex + 1];
            const firstTask = this._getFirstTaskOfFlowType(nextFlowType);
            if (firstTask) this.currentRoute = firstTask.routeName;
            return firstTask;
        },
    },
});
